import React, { useEffect, useState } from 'react';
import Styles from '../../assets/css/components/warehouse/warehouseEdit.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setShouldRefresh } from '../../features/warehouse/warehouseSlice';
import { popUpBoxTrigger } from '../../features/message/popUpSlice';
import ValidationHandler from '../../utils/validationHandler';
import PopUpMessage from '../../utils/errors/PopUpMessage';
import Loader from '../../utils/Loader';
import { getWarehouseById, updateWarehouse, getCountries, getStates, getCities, checkWarehouseStatus } from '../../services/Api';
import { AiOutlineLoading } from 'react-icons/ai';

const WarehouseEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [originalData, setOriginalData] = useState(null);

    // State for dropdown data
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        address: '',
        country_id: '',
        state_id: '',
        city_id: '',
        status: ''
    });

    const [errors, setErrors] = useState({});

    const validationRules = {
        name: {
            required: true,
            message: 'Please enter a warehouse name'
        },
        address: {
            required: true,
            message: 'Please enter a warehouse address'
        },
        country_id: {
            required: true,
            message: 'Please select a warehouse country'
        },
        state_id: {
            required: true,
            message: 'Please select a warehouse state',
            validate: (value) => {
                return value !== '';
            }
        },
        city_id: {
            required: true,
            message: 'Please select a warehouse city',
            validate: (value) => {
                return value !== '';
            }
        },
        status: {
            required: true,
            message: 'Please select a warehouse status'
        }
    };

    // New function to handle country change
    const handleCountryChange = async (countryId, isUserChange = true) => {
        if (!countryId) {
            setStates([]);
            setCities([]);
            if (isUserChange) {
                setFormData(prev => ({
                    ...prev,
                    state_id: '',
                    city_id: ''
                }));
            }
            return;
        }

        setLoadingStates(true);
        setStates([]);
        setCities([]);

        try {
            // Find the country object to get its code
            const country = countries.find(c => c.id == countryId);

            if (!country) {
                console.error('Country not found for ID:', countryId);
                setStates([]);
                return;
            }

            // Get country code for API call
            const countryCode = country.code || country.country_code;

            if (!countryCode) {
                console.error('No country code found for country:', country);
                setStates([]);
                return;
            }

            // Fetch states using country code
            const response = await getStates(countryCode);

            if (response.status === 200) {
                let statesData = [];

                // Handle different response structures
                if (Array.isArray(response.data)) {
                    statesData = response.data;
                } else if (response.data && Array.isArray(response.data.states)) {
                    statesData = response.data.states;
                } else if (response.data && Array.isArray(response.data.data)) {
                    statesData = response.data.data;
                }

                if (statesData.length === 0) {
                    setStates([{
                        id: 'no-states',
                        name: 'No states available',
                        disabled: true
                    }]);
                } else {
                    setStates(statesData);

                    // If this is initial load (not user change), we need to fetch cities for existing state
                    if (!isUserChange && formData.state_id) {
                        setTimeout(() => {
                            handleStateChange(formData.state_id, false);
                        }, 300);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching states:', error);
            setStates([{
                id: 'no-states',
                name: 'No states available',
                disabled: true
            }]);
        } finally {
            setLoadingStates(false);
        }
    };

    // New function to handle state change
    const handleStateChange = async (stateId, isUserChange = true) => {
        if (!stateId || stateId === 'no-states') {
            setCities([]);
            if (isUserChange) {
                setFormData(prev => ({
                    ...prev,
                    city_id: ''
                }));
            }
            return;
        }

        setLoadingCities(true);
        setCities([]);

        try {
            // Fetch cities for the state
            const response = await getCities(stateId);

            if (response.status === 200) {
                let citiesData = [];

                // Handle different response structures
                if (Array.isArray(response.data)) {
                    citiesData = response.data;
                } else if (response.data && Array.isArray(response.data.cities)) {
                    citiesData = response.data.cities;
                } else if (response.data && Array.isArray(response.data.data)) {
                    citiesData = response.data.data;
                }

                if (citiesData.length === 0) {
                    setCities([{
                        id: 'no-cities',
                        name: 'No cities available',
                        disabled: true
                    }]);
                } else {
                    setCities(citiesData);
                }
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            setCities([{
                id: 'no-cities',
                name: 'No cities available',
                disabled: true
            }]);
        } finally {
            setLoadingCities(false);
        }
    };

    // Fetch countries on component mount
    useEffect(() => {
        fetchCountries();
    }, []);

    // Fetch warehouse data on component mount
    useEffect(() => {
        const fetchWarehouseData = async () => {
            setIsLoading(true);
            try {
                // Fetch warehouse data
                const response = await getWarehouseById(id);
                if (response.status === 200) {
                    const data = response.data;
                    setOriginalData(data);

                    // Set initial form data
                    setFormData({
                        name: data.name,
                        address: data.address,
                        country_id: data.country_id, // Numeric ID from API
                        state_id: data.state_id,
                        city_id: data.city_id,
                        status: data.status
                    });

                    // First, ensure countries are loaded
                    await fetchCountries();

                    // After countries are loaded, fetch states for the country
                    if (data.country_id) {
                        // Wait a moment for countries to be set in state
                        setTimeout(() => {
                            handleCountryChange(data.country_id, false);
                        }, 300);
                    }
                }
            } catch (error) {
                console.error('Error fetching warehouse:', error);
                showPopup('error', 'Error', 'Failed to fetch warehouse details');
            } finally {
                setIsLoading(false);
            }
        };

        fetchWarehouseData();
    }, [id]);

    // Add this useEffect to handle initial state/city loading after countries are loaded
    useEffect(() => {
        if (formData.country_id && countries.length > 0 && originalData) {
            // Only fetch states if we haven't already loaded them
            if (states.length === 0) {
                // Find country to get its code
                const country = countries.find(c => c.id == formData.country_id);
                if (country) {
                    const countryCode = country.code || country.country_code;
                    if (countryCode) {
                        // Fetch states for the initial country
                        getStates(countryCode).then(response => {
                            if (response.status === 200) {
                                let statesData = [];

                                if (Array.isArray(response.data)) {
                                    statesData = response.data;
                                } else if (response.data && Array.isArray(response.data.states)) {
                                    statesData = response.data.states;
                                } else if (response.data && Array.isArray(response.data.data)) {
                                    statesData = response.data.data;
                                }

                                if (statesData.length > 0) {
                                    setStates(statesData);

                                    // After states are loaded, fetch cities for the initial state
                                    if (formData.state_id) {
                                        setTimeout(() => {
                                            getCities(formData.state_id).then(cityResponse => {
                                                if (cityResponse.status === 200) {
                                                    let citiesData = [];

                                                    if (Array.isArray(cityResponse.data)) {
                                                        citiesData = cityResponse.data;
                                                    } else if (cityResponse.data && Array.isArray(cityResponse.data.cities)) {
                                                        citiesData = cityResponse.data.cities;
                                                    } else if (cityResponse.data && Array.isArray(cityResponse.data.data)) {
                                                        citiesData = cityResponse.data.data;
                                                    }

                                                    if (citiesData.length > 0) {
                                                        setCities(citiesData);
                                                    }
                                                }
                                            });
                                        }, 300);
                                    }
                                }
                            }
                        });
                    }
                }
            }
        }
    }, [countries, formData.country_id, originalData]);

    // Fetch countries
    const fetchCountries = async () => {
        setLoadingCountries(true);
        try {
            const response = await getCountries();
            if (response.status === 200) {
                let countriesData = [];

                // Handle different response structures
                if (Array.isArray(response.data)) {
                    countriesData = response.data;
                } else if (response.data && Array.isArray(response.data.countries)) {
                    countriesData = response.data.countries;
                } else if (response.data && Array.isArray(response.data.data)) {
                    countriesData = response.data.data;
                }

                setCountries(countriesData);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
            showPopup('error', 'Error', 'Failed to fetch countries');
            setCountries([]);
        } finally {
            setLoadingCountries(false);
        }
    };

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    };

    // Validate form data
    const validateForm = () => {
        const validationErrors = {};

        // Check each validation rule
        Object.keys(validationRules).forEach(field => {
            const rule = validationRules[field];
            const value = formData[field];

            // Skip validation for state_id if it's "no-states" option
            if (field === 'state_id' && value === 'no-states') {
                return;
            }

            // Skip validation for city_id if it's "no-cities" option
            if (field === 'city_id' && value === 'no-cities') {
                return;
            }

            // Required field validation - Fixed for status field
            if (rule.required) {
                if (field === 'status') {
                    if (value === null || value === undefined || value === '') {
                        validationErrors[field] = rule.message;
                    }
                } else {
                    // For other fields, use the original logic
                    if (!value || value.toString().trim() === '') {
                        validationErrors[field] = rule.message;
                    }
                }
            }

            // Check custom validation if exists
            // if (rule.validate && !rule.validate(value)) {
            //     validationErrors[field] = rule.message;
            // }
        });

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleInputChange = async (e) => {
        const { name, value } = e.target;

        // Update form data
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'country_id' && { state_id: '', city_id: '' }),
            ...(name === 'state_id' && { city_id: '' })
        }));

        // Handle select styling
        const selectElement = e.target;
        if (value && value !== 'no-states' && value !== 'no-cities') {
            selectElement.style.color = '#000000';
        } else {
            selectElement.style.color = '#797775';
        }

        // Clear specific field error
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Handle dependent dropdown fetching
        if (name === 'country_id') {
            await handleCountryChange(value, true);
        } else if (name === 'state_id') {
            if (value === 'no-states') {
                setCities([{
                    id: 'no-cities',
                    name: 'No cities available',
                    disabled: true
                }]);
            } else {
                await handleStateChange(value, true);
            }
        }
    };

    const handleSubmit = async () => {
        // Validate form before submission
        if (!validateForm()) {
            showPopup('error', 'Validation Error', 'Please fill all required fields correctly');
            return;
        }

        if (formData.state_id === 'no-states' || formData.city_id === 'no-cities') {
            showPopup('error', 'Validation Error', 'Selected location does not have states or cities available. Please select a different country.');
            return;
        }

        // Check if status is being changed to inactive
        const isChangingToInactive = originalData?.status === 1 && formData.status === "0";

        setLoading(true);
        try {
            // If changing to inactive, first check warehouse status
            if (isChangingToInactive) {
                try {
                    await checkWarehouseStatus(id, { status: formData.status });
                } catch (error) {
                    if (error.response?.status === 422) {
                        const errorMessage = error.response?.data?.message || 'This warehouse is associated with other products and cannot be deactivated.';
                        showPopup('error', 'Cannot Deactivate', errorMessage);
                        setLoading(false);
                        return;
                    }
                    throw error;
                }
            }

            // No need to convert - we're already using numeric ID
            const response = await updateWarehouse({
                id: id,
                name: formData.name,
                address: formData.address,
                country_id: formData.country_id, // Already numeric ID
                state_id: formData.state_id,
                city_id: formData.city_id,
                status: formData.status
            });

            if (response.status === 200) {
                showPopup('success', 'Success', 'Warehouse updated successfully');
                dispatch(setShouldRefresh(true));
                await new Promise(resolve => setTimeout(resolve, 1000));
                navigate("/warehouse");
            }
        } catch (error) {
            console.error('Error updating warehouse:', error);
            const errorMessage = error.response?.data?.message || 'Failed to update warehouse';
            showPopup('error', 'Error', errorMessage);

            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`${Styles.warehouse_edit}`}>
            <PopUpMessage />
            <div className={`${Styles.heading}`}>
                <h2>Warehouse Information</h2>
            </div>

            <div className={`${Styles.warehouse_form}`}>
                {isLoading ? (
                    <div className={`${Styles.form_loader_container}`}>
                        <div>
                            <Loader size="large" color="primary" />
                        </div>
                    </div>
                ) : (
                    <div className={`${Styles.warehouse_details}`}>
                        <div className={`${Styles.title}`}>
                            <h4>Warehouse details</h4>
                            <hr />
                        </div>
                        <div className={`${Styles.form}`}>
                            <div className={`${Styles.label_input} ${errors.name ? Styles.error : ''}`}>
                                <label>Warehouse Name<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        placeholder="Enter Warehouse Name"
                                        onChange={handleInputChange}
                                    />
                                    {errors.name && <p className={`${Styles.errorMessage}`}>{errors.name}</p>}
                                </div>
                            </div>

                            <div className={`${Styles.label_input} ${errors.address ? Styles.error : ''}`}>
                                <label>Warehouse Address<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        placeholder="Enter Address"
                                        onChange={handleInputChange}
                                    />
                                    {errors.address && <p className={`${Styles.errorMessage}`}>{errors.address}</p>}
                                </div>
                            </div>

                            <div className={`${Styles.label_input} ${errors.country_id ? Styles.error : ''}`}>
                                <label>Country / State / City<span>*</span></label>
                                <div className={`${Styles.select_container}`}>
                                    <div className={`${Styles.country} ${errors.country_id ? Styles.error : ''}`}>
                                        <select
                                            className={`${Styles.country} ${errors.country_id ? Styles.error : ''}`}
                                            name="country_id"
                                            value={formData.country_id}
                                            onChange={handleInputChange}
                                            disabled={loadingCountries}
                                        >
                                            <option value="" hidden>
                                                {loadingCountries ? 'Loading...' : 'Select Country'}
                                            </option>
                                            {Array.isArray(countries) && countries.map((country) => (
                                                <option key={country.id} value={country.id}>
                                                    {country.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.country_id && <p className={`${Styles.errorMessage}`}>{errors.country_id}</p>}
                                    </div>

                                    <div className={`${Styles.state} ${errors.state_id ? Styles.error : ''}`}>
                                        <select
                                            className={`${Styles.state}`}
                                            name="state_id"
                                            value={formData.state_id}
                                            onChange={handleInputChange}
                                            disabled={!formData.country_id || loadingStates}
                                        >
                                            <option value="" hidden>
                                                {loadingStates ? 'Loading...' : 'Select State'}
                                            </option>
                                            {Array.isArray(states) && states.map((state) => (
                                                <option key={state.id} value={state.id} disabled={state.disabled || false}>
                                                    {state.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.state_id && <p className={`${Styles.errorMessage}`}>{errors.state_id}</p>}
                                    </div>

                                    <div className={`${Styles.city} ${errors.city_id ? Styles.error : ''}`}>
                                        <select
                                            className={`${Styles.city}`}
                                            name="city_id"
                                            value={formData.city_id}
                                            onChange={handleInputChange}
                                            disabled={!formData.state_id || loadingCities || formData.state_id === 'no-states'}
                                        >
                                            <option value="" hidden>
                                                {loadingCities ? 'Loading...' : formData.state_id === 'no-states' ? 'No cities available' : 'Select City'}
                                            </option>
                                            {Array.isArray(cities) && cities.map((city) => (
                                                <option key={city.id} value={city.id} disabled={city.disabled || false}>
                                                    {city.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.city_id && <p className={`${Styles.errorMessage}`}>{errors.city_id}</p>}
                                    </div>
                                </div>
                            </div>

                            <div className={`${Styles.label_input} ${errors.status ? Styles.error : ''}`}>
                                <label>Status<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        className={`${errors.status ? Styles.error : ''}`}
                                    >
                                        <option value="" disabled hidden>Select Status</option>
                                        <option value="1">Active</option>
                                        <option value="0">Inactive</option>
                                    </select>
                                    {errors.status && <p className={`${Styles.errorMessage}`}>{errors.status}</p>}
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.buttons}`}>
                            <button onClick={handleSubmit} disabled={loading}>
                                {loading ? <AiOutlineLoading className="animate-spin" /> : "Update"}
                            </button>
                            <button onClick={() => navigate("/warehouse")}>Cancel</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WarehouseEdit;
